import { t } from "i18next";
import { FaArrowDown, FaArrowUp, FaPhone } from "react-icons/fa6";
import { cn, getFloorName } from "@/lib/utils";
import Globe from "@/assets/globe-white.svg";
import { GiBodyHeight, GiTicket } from "react-icons/gi";
import { LuSunSnow } from "react-icons/lu";
import queueTime from "@/assets/queue-time.svg";
import info from "@/assets/information.svg";
import { Button } from "./ui/button";
import { QueueTime } from "@/hooks/useQueueTimes";
import useMapStore from "@/store/mapStore";
import PoiDetailsRouteProgress from "./PoiDetailsRouteProgress";

function PoiDetailsSpecs({ showMore }: { showMore: boolean }) {
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const haveRoutePreview = useMapStore((state) => state.haveRoutePreview);
  const routeDetails = useMapStore((state) => state.routeDetails);
  const currentLanguage = useMapStore((state) => state.currentLang);
  const floors = useMapStore((state) => state.floors);
  const kioskMode = useMapStore((state) => state.kioskMode);
  // const activeKiosk = useMapStore((state) => state.activeKiosk);
  const mobileMode = useMapStore((state) => state.mobileMode);
  let poi = useMapStore((state) => state.routeFinish);
  const activeStop = useMapStore((state) => state.activeStop);
  const stops = useMapStore((state) => state.stops);
  const filterCategories = useMapStore((state) => state.filterCategories);

  if (stops.length > 2 && stops[activeStop]?.feature) {
    poi = stops[activeStop].feature!;
  }

  const getUrl = () => {
    if (poi?.properties) {
      if (poi.properties.url_i18n && poi.properties.url_i18n[currentLanguage]) {
        return poi.properties.url_i18n[currentLanguage];
      }
      if (poi.properties.url) {
        return poi.properties.url;
      }
      if (poi.properties.metadata?.url) {
        return poi.properties.metadata.url;
      }
    }
  };

  const getColorByQueue = (queue: QueueTime) => {
    const amenity = filterCategories
      .find((i) => i.title === "live-queue-times")!
      .items.find((i) => (i.id as string) === queue.queueAmenityId);
    return amenity?.color;
  };

  return (
    <>
      {!haveRouteDetails && (
        <>
          <ul className="mt-2 flex gap-3 kiosk:float-right kiosk:block">
            {poi?.properties?.contact &&
              poi?.properties?.contact.length > 0 &&
              poi?.properties?.contact !== "TBA" && (
                <li className="mb-2 inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-primary px-1 py-2 kiosk:gap-5 kiosk:px-8">
                  <FaPhone className="text-primary" />
                  <span className="text-sm">
                    {mobileMode ? (
                      <a href={`tel:${poi?.properties.contact}`}>
                        {t("call-store")}
                      </a>
                    ) : (
                      <span dir="ltr">{poi?.properties.contact}</span>
                    )}
                  </span>
                </li>
              )}
            <li
              className={cn(
                "mb-2 inline-flex",
                poi?.properties?.contact &&
                  poi?.properties?.contact.length > 0 &&
                  poi?.properties?.contact !== "TBA" &&
                  "flex-1",
              )}
            >
              {!kioskMode && getUrl() && showMore && (
                <a
                  href={getUrl()}
                  target="_parent"
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-full border border-primary bg-primary px-4 py-2 text-sm text-white lg:hover:bg-primary/70",
                    poi?.properties?.contact &&
                      poi?.properties?.contact.length > 0 &&
                      poi?.properties?.contact !== "TBA" &&
                      "flex-1 px-1",
                  )}
                >
                  <img src={Globe} alt="Globe" className="-mt-0.5" />
                  {t("check-website")}
                </a>
              )}
            </li>
          </ul>
          <ul className="mb-5 mt-2 text-xs font-bold text-secondary lg:text-sm">
            {showMore && poi.properties?.metadata?.height_limit && (
              <li className="mb-2 flex items-center">
                <Button
                  size="iconSm"
                  className="mr-2 flex-none cursor-auto rounded-full bg-secondary hover:bg-secondary"
                >
                  <GiBodyHeight />
                </Button>
                <span>{poi.properties?.metadata?.height_limit}</span>
              </li>
            )}
            {poi.properties?._queue?.value !== undefined && (
              <li className="mb-2 flex items-center">
                <Button
                  size="iconSm"
                  className="mr-2 flex-none cursor-auto rounded-full bg-[var(--custom-color)] hover:bg-[var(--custom-color)]"
                  style={{
                    // @ts-expect-error: custom property
                    "--custom-color": getColorByQueue(poi.properties?._queue),
                  }}
                >
                  <img src={queueTime} alt="Queue time" className="w-4/6" />
                </Button>
                Queue time: {poi.properties?._queue?.value} mins
              </li>
            )}
            {(poi.properties?.metadata?.seasonal === true ||
              poi.properties?.metadata?.seasonal === "true") && (
              <li className="mb-2 flex items-center">
                <Button
                  size="iconSm"
                  className="mr-2 flex-none cursor-auto rounded-full bg-primary hover:bg-primary"
                >
                  <LuSunSnow />
                </Button>
                Seasonal attraction
              </li>
            )}
            {poi.properties?._queue?.statusOpen !== undefined && (
              <li className="mb-2 flex items-center">
                <Button
                  size="iconSm"
                  className="mr-2 flex-none cursor-auto rounded-full bg-primary hover:bg-primary"
                >
                  <img src={info} alt="Ride status" className="w-4/6" />
                </Button>
                Ride status:{" "}
                {poi.properties?._queue?.statusOpen ? "Open" : "Closed"}
              </li>
            )}
            {showMore && poi.properties?.metadata?.ticket_type && (
              <li className="mb-2 flex items-center">
                <Button
                  size="iconSm"
                  className="mr-2 flex-none cursor-auto rounded-full bg-primary hover:bg-primary"
                >
                  <GiTicket />
                </Button>
                Ticket type: {poi.properties?.metadata?.ticket_type}
              </li>
            )}
          </ul>
        </>
      )}
      {(haveRoutePreview || haveRouteDetails) && (
        <ul
          className={cn(
            "my-5 flex flex-col gap-2 text-sm lg:gap-1 lg:text-base",
            haveRouteDetails && "mt-0",
            haveRouteDetails && mobileMode && "my-1.5",
          )}
        >
          {/*haveRoutePreview && !haveRouteDetails && (
            <li className="flex items-center gap-2">
              <FaLocationCrosshairs className="text-lg text-primary" />
              <span>
                {t("you-are-on")}{" "}
                <strong>
                  {getFloorName({
                    floor:
                      kioskMode && activeKiosk
                        ? floors.find((f) => f.level === activeKiosk.level)!
                        : floors.find(
                            (f) =>
                              f.id === routeDetails.start?.properties?.floor_id,
                          )!,
                    language: currentLanguage,
                  })}{" "}
                  {t("floor").toLowerCase()}
                </strong>
              </span>
            </li>
          )*/}
          {haveRoutePreview &&
            !haveRouteDetails &&
            routeDetails.start?.properties?.level !==
              routeDetails.end?.properties?.level && (
              <li className="flex items-center gap-2">
                {routeDetails.start?.properties?.level <
                routeDetails.end?.properties?.level ? (
                  <FaArrowUp className="text-lg text-primary" />
                ) : (
                  <FaArrowDown className="text-lg text-primary" />
                )}
                <span>
                  {routeDetails.start?.properties?.level <
                  routeDetails.end?.properties?.level
                    ? t("ascend")
                    : t("descend")}{" "}
                  <strong>
                    {Math.abs(
                      routeDetails.start?.properties?.level -
                        routeDetails.end?.properties?.level,
                    )}{" "}
                    {Math.abs(
                      routeDetails.start?.properties?.level -
                        routeDetails.end?.properties?.level,
                    ) > 1
                      ? t("floors")
                      : t("floor").toLowerCase()}{" "}
                    {`${t("to")} ${getFloorName({
                      floor: floors.find(
                        (f) => f.level === routeDetails.end?.properties.level,
                      )!,
                      language: currentLanguage,
                    })}`}{" "}
                    {t("floor").toLowerCase()}
                  </strong>
                </span>
              </li>
            )}
          {haveRoutePreview && (
            <li className="flex items-center gap-2">
              <PoiDetailsRouteProgress />
            </li>
          )}
        </ul>
      )}
    </>
  );
}

export default PoiDetailsSpecs;
